// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { ProviderAccessDocumentErrorIllustration } from '@loa/ui-components';
import { ProviderAccessPageShell } from './ProviderAccessPageShell';
import { ProviderAccessStatusCard } from './ProviderAccessStatusCard';

type ProviderAccessRejectedProps = Readonly<{
  brokerageName: string;
  brokerageLogoUrl: string | null;
  /** Timestamp when the LOA was rejected (for returning visitors) */
  rejectedAt?: string | null;
  /** When 'COA', shows Change of Agency wording instead of LOA wording */
  documentType?: 'LOA' | 'COA' | 'NEW_BUSINESS';
}>;

const rejectedIcon = <ProviderAccessDocumentErrorIllustration />;

export function ProviderAccessRejected({
  brokerageName,
  brokerageLogoUrl,
  rejectedAt,
  documentType,
}: ProviderAccessRejectedProps) {
  const isCoA = documentType === 'COA';
  const documentLabel = isCoA ? 'change of agency' : 'letter of authority';

  const message = rejectedAt
    ? (() => {
        let dateText = '';
        try {
          const date = new Date(rejectedAt);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          dateText = ` on ${day}/${month}/${year}`;
        } catch {
          // ignore
        }
        return `This ${documentLabel} was rejected${dateText}. Please contact the brokerage for assistance if you need to revoke the rejection.`;
      })()
    : "It has been passed to the relevant financial advisor. You don't need to do anything else.";

  const title = isCoA ? 'Change of Agency rejected' : 'Letter of Authority rejected';

  return (
    <ProviderAccessPageShell
      brokerageName={brokerageName}
      brokerageLogoUrl={brokerageLogoUrl}
    >
      <ProviderAccessStatusCard
        icon={rejectedIcon}
        title={title}
        message={message}
      />
    </ProviderAccessPageShell>
  );
}
